import {Button, Flex, Group, Text, Textarea} from "@mantine/core";
import {useRef, useState, useEffect} from "react";
import {getHotkeyHandler, useElementSize, useMediaQuery} from "@mantine/hooks";
import {useAppSelector} from "../../../app/hooks";
import {useTranslation} from "react-i18next";
import {ChatInputTypePicker} from "./ChatInputTypePicker";
import {streamController} from "../chatApi";
import IconArrowRight from "../../../assets/arrow-right.svg?react";
import IconArrowUp from "../../../assets/arrow-up.svg?react";
import IconPlayerStop from "../../../assets/player-stop.svg?react";
import classes from "./CommandInput.module.css";

interface CommandInputProps {
    variant: "splash" | "chat";
    onSendMessage(message: string): void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onChange?(value: string): void;
    placeholder?: string;
    disabled?: boolean;
}

export function CommandInput({
    variant,
    onSendMessage,
    onChange,
    onKeyDown,
    placeholder,
    disabled = false,
}: CommandInputProps) {
    const {t} = useTranslation();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [value, setValue] = useState("");
    const isDesktop = useMediaQuery("(min-width: $mantine-breakpoint-sm)");
    const isStreaming = useAppSelector((state) => state.chat.isStreaming);

    // Auto-focus on desktop
    useEffect(() => {
        if (isDesktop) {
            inputRef.current?.focus();
        }
    }, [isDesktop]);

    const handleOnChange = (value: string) => {
        setValue(value);
        onChange?.(value);
    };

    const handleSubmit = () => {
        if (isStreaming || value.trim() === "") {
            return;
        }
        onSendMessage(value);
        setValue("");
        if (isDesktop) {
            inputRef.current?.focus();
        }
    };

    const cancelStreaming = () => {
        streamController?.abort();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Call custom onKeyDown first if provided
        onKeyDown?.(event);

        // If not prevented, handle enter key for submit
        if (!event.defaultPrevented) {
            const hotkeyHandler = getHotkeyHandler([["enter", handleSubmit]]);
            hotkeyHandler(event);
        }
    };

    // Dynamic wrapping calculation
    const {ref: wrapperRef, width: wrapperWidth} = useElementSize();
    const {ref: hiddenRef, width: hiddenWidth} = useElementSize();
    const {ref: buttonGroupRef, width: buttonGroupWidth} = useElementSize();
    const inputHasLineBreak = value.includes("\n");
    const denominator = wrapperWidth - buttonGroupWidth;
    const shouldWrap =
        (denominator > 0 && hiddenWidth / denominator > 0.9) ||
        inputHasLineBreak;

    // Determine variant-specific classes
    const outerGroupClass = `${classes.outerGroup} ${classes[`outerGroup--${variant}`]}`;
    const wrapClass = shouldWrap ? classes.outerGroupWrap : "";
    const inputClass = `${classes.input} ${classes[`input--${variant}`]}`;

    // Default placeholders based on variant
    const finalPlaceholder =
        placeholder ||
        (variant === "splash"
            ? t("common.askQuestion")
            : t("input.placeholder.thread"));

    // Determine button behavior and icon
    const isButtonDisabled = isStreaming ? false : value.trim() === "";
    const buttonAction = isStreaming ? cancelStreaming : handleSubmit;
    const buttonTitle = isStreaming ? "Stop" : "Send";

    return (
        <Flex
            ref={wrapperRef}
            className={`${outerGroupClass} ${wrapClass}`}
            gap="xs"
        >
            <Textarea
                ref={inputRef}
                disabled={disabled}
                className={classes.textareaWrapper}
                classNames={{
                    input: inputClass,
                }}
                placeholder={finalPlaceholder}
                value={value}
                autosize
                minRows={1}
                autoFocus={isDesktop}
                maxRows={13}
                onChange={(e) => handleOnChange(e.target.value)}
                onKeyDown={handleKeyDown}
            />
            <Text ref={hiddenRef} className={classes.hiddenText}>
                {value}
            </Text>
            <Group
                ref={buttonGroupRef}
                className={classes.buttonGroup}
                gap="xs"
                wrap="nowrap"
                justify="flex-end"
            >
                <Button
                    className={classes.sendButton}
                    classNames={{
                        inner: classes.sendButtonInner,
                    }}
                    variant="subtle"
                    onClick={buttonAction}
                    disabled={isButtonDisabled}
                    title={buttonTitle}
                >
                    {isStreaming ? (
                        <IconPlayerStop className={classes.icon} />
                    ) : (
                        <>
                            <IconArrowRight
                                className={`${classes.icon} ${classes.iconArrowRight}`}
                            />
                            <IconArrowUp
                                className={`${classes.icon} ${classes.iconArrowUp}`}
                            />
                        </>
                    )}
                </Button>
            </Group>
        </Flex>
    );
}
